import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "../../services/api";
import toast from "react-hot-toast";
import AdminLayout from "../layouts/AdminLayout";
import PageTitle from "../components/ui/PageTitle";
import DataTable from "../components/DataTable";
import { Plus, Building2, ShieldCheck } from "lucide-react";

const formatDate = (value) =>
  value ? new Date(value).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" }) : "";

export default function Jobs() {
  const navigate = useNavigate();
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchJobs();
  }, []);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("admin_token");
      const response = await api.get("/jobs", { headers: { Authorization: `Bearer ${token}` } });
      setJobs(response.data.data || response.data); 
    } catch (error) {
      toast.error("Failed to load jobs.");
    } finally {
      setLoading(false); 
    }
  };

  const handleDelete = async (job) => {
    if (window.confirm(`Are you sure you want to delete "${job.title}"?`)) {
      try {
        const token = localStorage.getItem("admin_token"); 
        await api.delete(`/jobs/${job.id}`, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Job deleted successfully");
        setJobs(jobs.filter(j => j.id !== job.id));
      } catch (error) {
        toast.error("Failed to delete job");
      }
    }
  };

  const getFilteredJobs = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); 

    return jobs.filter((job) => {
        if (!job.deadline) return true; 
        const deadlineDate = new Date(job.deadline);
        if (location.pathname === "/jobs/active") return deadlineDate >= today; 
        if (location.pathname === "/jobs/expired") return deadlineDate < today; 
        return true; 
      }).map((job) => {
        if (job.deadline && new Date(job.deadline) < today) {
            return { ...job, status: "expired" };
        }
        return job;
      });
  };

  const pageTitle = location.pathname === "/jobs/active" ? "Active Jobs" : location.pathname === "/jobs/expired" ? "Expired Jobs" : "Job Listings";
  const pageSubtitle = location.pathname === "/jobs/active" ? "Manage currently open job vacancies." : location.pathname === "/jobs/expired" ? "View jobs that have passed their deadline." : "Manage all your active and expired job vacancies here.";

  const columns = [
    { key: "title", label: "Job Title" },
    { key: "category", label: "Category" },
    { 
      key: "employer", 
      label: "Created By", 
      format: (emp) => emp && (emp.company_name || emp.name) 
        ? <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-blue-50 text-blue-700 border border-blue-200"><Building2 size={12} /> {emp.company_name || emp.name}</span>
        : <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-50 text-amber-700 border border-amber-200"><ShieldCheck size={12} /> Admin</span>
    },
    { key: "experience", label: "Experience" },
    { key: "deadline", label: "Deadline", format: formatDate },
    { key: "status", label: "Status" },
  ];

  return (
    <AdminLayout>
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <PageTitle title={pageTitle} subtitle={pageSubtitle} />
        <button onClick={() => navigate("/jobs/new")} className="flex items-center gap-2 bg-yellow-500 hover:bg-yellow-600 text-black font-semibold py-2 px-6 rounded-lg transition-colors cursor-pointer shadow-sm">
          <Plus size={18} /> Add New Job
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div></div>
      ) : (
        <DataTable
          title={pageTitle}
          columns={columns}
          data={getFilteredJobs()} 
          onView={(row) => navigate(`/jobs/view/${row.id}`)} // 🌟 Naye page par bhej rahe hain
          onEdit={(row) => navigate(`/jobs/edit/${row.id}`)} 
          onDelete={handleDelete} 
        />
      )}
    </AdminLayout>
  );
}